import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { AgentAvatar } from './AgentAvatars';
import { Check, ArrowRight, ArrowLeft, Rocket, Star } from 'lucide-react';

export default function DeployWizard() {
  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [customInstructions, setCustomInstructions] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [deployed, setDeployed] = useState(false);
  const [deployedAgent, setDeployedAgent] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/agents/catalog').then(res => setCatalog(res.data || [])).catch(console.error);
  }, []);

  const handleDeploy = async () => {
    setDeploying(true);
    setError('');
    try {
      const res = await api.post('/api/agents/deploy', {
        agent_type: selectedAgent.id,
        custom_instructions: customInstructions || null,
      });
      setDeployedAgent(res.data);
      setDeployed(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to deploy agent');
    } finally {
      setDeploying(false);
    }
  };

  const steps = [
    { num: 1, label: 'Select Role' },
    { num: 2, label: 'Configure' },
    { num: 3, label: 'Deploy' },
  ];

  if (deployed && deployedAgent) {
    return (
      <div className="wizard-container fade-in">
        <div className="wizard-card">
          <div className="deploy-success">
            <div className="success-icon"><Check size={36} /></div>
            <h3>{selectedAgent?.name} is Ready!</h3>
            <p>Your AI {selectedAgent?.title} has been successfully deployed and is ready to start working.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button className="btn-action" onClick={() => navigate(`/workspace/${deployedAgent.id}`)} data-testid="go-to-workspace">
                Open Workspace <ArrowRight size={16} style={{ marginLeft: 6 }} />
              </button>
              <button className="btn-secondary" onClick={() => navigate('/')} data-testid="go-to-dashboard">
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-container fade-in">
      {/* Steps indicator */}
      <div className="wizard-steps">
        {steps.map((s, i) => (
          <React.Fragment key={s.num}>
            <div className="wizard-step">
              <div className={`step-number ${step > s.num ? 'completed' : step === s.num ? 'active' : ''}`}>
                {step > s.num ? <Check size={16} /> : s.num}
              </div>
              <span className={`step-label ${step === s.num ? 'active' : ''}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className={`step-connector ${step > s.num ? 'completed' : ''}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Select Role */}
      {step === 1 && (
        <div className="wizard-card slide-up">
          <h3>Choose Your AI Employee</h3>
          <p className="wizard-subtitle">Select the AI role that best fits your HR needs. Available roles are marked with a star.</p>
          {error && <div className="error-msg">{error}</div>}
          <div className="role-grid">
            {catalog.map(agent => (
              <div key={agent.id}
                className={`role-card ${selectedAgent?.id === agent.id ? 'selected' : ''} ${!agent.available ? 'unavailable' : ''}`}
                onClick={() => agent.available && setSelectedAgent(agent)}
                data-testid={`role-${agent.id}`}>
                <div className="role-card-header">
                  <div className="role-avatar">
                    <AgentAvatar agentType={agent.id} size={44} />
                  </div>
                  <div>
                    <h4 style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {agent.name}
                      {agent.available && <Star size={14} fill="#F9A825" color="#F9A825" />}
                    </h4>
                    <p className="role-title" style={{ color: agent.color }}>{agent.title}</p>
                  </div>
                </div>
                <p className="role-desc">{agent.description}</p>
                {!agent.available && <span className="coming-soon-badge">Coming Soon</span>}
              </div>
            ))}
          </div>
          <div className="wizard-actions">
            <button className="btn-secondary" onClick={() => navigate('/')}>Cancel</button>
            <button className="btn-action" disabled={!selectedAgent} onClick={() => { setStep(2); setError(''); }} data-testid="next-step">
              Next <ArrowRight size={16} style={{ marginLeft: 6 }} />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Configure */}
      {step === 2 && (
        <div className="wizard-card slide-up">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
            <AgentAvatar agentType={selectedAgent.id} size={64} />
            <div>
              <h3>Configure {selectedAgent.name}</h3>
              <p className="wizard-subtitle" style={{ marginBottom: 0 }}>{selectedAgent.title}</p>
            </div>
          </div>
          <div className="form-group">
            <label>Custom Instructions (Optional)</label>
            <textarea data-testid="custom-instructions"
              value={customInstructions} onChange={e => setCustomInstructions(e.target.value)}
              placeholder={`Add any specific instructions for ${selectedAgent.name}. For example:\n- Focus on tech industry compliance\n- Use formal tone\n- Reference our company handbook`}
              style={{ width: '100%', minHeight: 120, padding: 12, border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontFamily: 'inherit', resize: 'vertical', outline: 'none' }}
            />
          </div>
          <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Specialties</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {selectedAgent.specialties?.map(s => (
                <span key={s} style={{ padding: '4px 12px', background: 'white', borderRadius: 8, fontSize: 13, fontWeight: 500, border: '1px solid #e2e8f0' }}>{s}</span>
              ))}
            </div>
          </div>
          <div style={{ background: '#f0fdf4', borderRadius: 12, padding: 16 }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 6, color: '#166534' }}>Available Tasks</h4>
            {selectedAgent.task_templates?.map(t => (
              <div key={t.id} style={{ padding: '6px 0', fontSize: 13, color: '#374151' }}>
                <strong>{t.name}</strong> — {t.description}
              </div>
            ))}
          </div>
          <div className="wizard-actions">
            <button className="btn-secondary" onClick={() => setStep(1)}>
              <ArrowLeft size={16} style={{ marginRight: 6 }} />Back
            </button>
            <button className="btn-action" onClick={() => setStep(3)} data-testid="next-step-2">
              Next <ArrowRight size={16} style={{ marginLeft: 6 }} />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm & Deploy */}
      {step === 3 && (
        <div className="wizard-card slide-up">
          <h3>Confirm Deployment</h3>
          <p className="wizard-subtitle">Review and deploy your AI employee.</p>
          {error && <div className="error-msg">{error}</div>}
          <div style={{ background: '#f8fafc', borderRadius: 12, padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
              <AgentAvatar agentType={selectedAgent.id} size={72} />
              <div>
                <h4 style={{ fontSize: 20, fontWeight: 700 }}>{selectedAgent.name}</h4>
                <p style={{ color: selectedAgent.color, fontWeight: 600 }}>{selectedAgent.title}</p>
                <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{selectedAgent.description}</p>
              </div>
            </div>
            {customInstructions && (
              <div style={{ background: 'white', borderRadius: 8, padding: 12, border: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Custom Instructions:</p>
                <p style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{customInstructions}</p>
              </div>
            )}
          </div>
          <div className="wizard-actions">
            <button className="btn-secondary" onClick={() => setStep(2)}>
              <ArrowLeft size={16} style={{ marginRight: 6 }} />Back
            </button>
            <button className="btn-action purple" onClick={handleDeploy} disabled={deploying} data-testid="deploy-confirm">
              {deploying ? 'Deploying...' : <><Rocket size={16} style={{ marginRight: 6 }} />Deploy {selectedAgent.name}</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
